import React, { useState } from "react";
import { X, User, Lock, Mail, Save, Loader2, AlertCircle } from "lucide-react";
import { PatchAxios } from "../../helpers/axiosHelpers";
import { toast } from "sonner";
import useAuthStore from "../../store/authStore";

const ProfileSettings = ({ onClose }) => {
  const { user, updateUser } = useAuthStore();
  const [activeTab, setActiveTab] = useState("info"); // 'info' or 'password'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Profile Info Form
  const [profileData, setProfileData] = useState({
    fullName: user?.name || "",
    email: user?.email || "",
  });

  // Password Change Form
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handleProfileUpdate = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await PatchAxios("/adminuser/profile", {
        fullName: profileData.fullName.trim(),
        email: profileData.email.trim(),
      });

      if (response.success) {
        toast.success("Profile updated successfully");
        updateUser({
          ...user,
          fullName: profileData.fullName, // Pass as fullName for backend compatibility
          name: profileData.fullName, // Store as name in frontend
          email: profileData.email,
        });
        onClose();
      }
    } catch (err) {
      setError(err.response?.data?.msg || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    try {
      setLoading(true);
      setError(null);

      // Client-side validation
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setError("New passwords do not match");
        setLoading(false);
        return;
      }

      if (passwordData.newPassword.length < 8) {
        setError("Password must be at least 8 characters long");
        setLoading(false);
        return;
      }

      const response = await PatchAxios("/adminuser/change-password", {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });

      if (response.success) {
        toast.success("Password changed successfully");
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        onClose();
      }
    } catch (err) {
      setError(err.response?.data?.msg || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return "AD";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[60] flex items-center justify-center p-4 md:p-6">
      <div className="bg-white md:rounded-[2.5rem] w-full max-w-2xl h-full md:h-auto md:max-h-[90vh] shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="p-6 md:p-8 flex justify-between items-center border-b border-slate-50">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-subidha-blue text-white rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/20">
              <User size={24} />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-black text-slate-900">
                Profile Settings
              </h2>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mt-0.5">
                Manage Your Account
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-3 hover:bg-slate-100 rounded-2xl transition-all"
          >
            <X className="w-6 h-6 text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-6">
          {/* Profile Avatar */}
          <div className="flex flex-col items-center space-y-4 pb-6 border-b border-slate-100">
            <div className="w-24 h-24 bg-gradient-to-br from-subidha-blue to-blue-600 rounded-3xl flex items-center justify-center text-white font-black text-3xl shadow-xl shadow-blue-500/20">
              {getInitials(user?.name)}
            </div>
            <div className="text-center">
              <h3 className="text-lg font-black text-slate-900">
                {user?.name || "Admin User"}
              </h3>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                {user?.role?.name || user?.role || "Administrator"}
              </p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex space-x-2 bg-slate-50 p-2 rounded-2xl">
            <button
              onClick={() => setActiveTab("info")}
              className={`flex-1 px-6 py-3 rounded-xl font-black text-xs uppercase tracking-wider transition-all ${
                activeTab === "info"
                  ? "bg-white text-subidha-blue shadow-lg shadow-slate-200/50"
                  : "text-slate-400 hover:text-slate-600"
              }`}
            >
              <Mail className="w-4 h-4 inline mr-2" />
              Profile Info
            </button>
            <button
              onClick={() => setActiveTab("password")}
              className={`flex-1 px-6 py-3 rounded-xl font-black text-xs uppercase tracking-wider transition-all ${
                activeTab === "password"
                  ? "bg-white text-subidha-blue shadow-lg shadow-slate-200/50"
                  : "text-slate-400 hover:text-slate-600"
              }`}
            >
              <Lock className="w-4 h-4 inline mr-2" />
              Change Password
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border-2 border-red-100 p-4 rounded-2xl flex items-center space-x-3 text-red-600 font-bold text-sm">
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}

          {/* Profile Info Form */}
          {activeTab === "info" && (
            <div className="space-y-5">
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1 mb-2 block">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={profileData.fullName}
                  onChange={(e) =>
                    setProfileData({ ...profileData, fullName: e.target.value })
                  }
                  placeholder="Enter your full name"
                  className="w-full bg-white border border-slate-200 px-5 py-4 rounded-2xl font-bold text-sm outline-none focus:ring-4 focus:ring-subidha-blue/10 focus:border-subidha-blue transition-all"
                />
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1 mb-2 block">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={profileData.email}
                  onChange={(e) =>
                    setProfileData({ ...profileData, email: e.target.value })
                  }
                  placeholder="Enter your email"
                  className="w-full bg-white border border-slate-200 px-5 py-4 rounded-2xl font-bold text-sm outline-none focus:ring-4 focus:ring-subidha-blue/10 focus:border-subidha-blue transition-all"
                />
              </div>

              <button
                onClick={handleProfileUpdate}
                disabled={loading}
                className="w-full px-6 py-4 rounded-2xl font-black text-white bg-subidha-blue hover:bg-slate-800 transition-all shadow-lg shadow-blue-500/20 disabled:opacity-50 uppercase text-xs tracking-widest flex items-center justify-center space-x-2"
              >
                {loading ? (
                  <Loader2 className="animate-spin" size={20} />
                ) : (
                  <>
                    <Save size={16} />
                    <span>Update Profile</span>
                  </>
                )}
              </button>
            </div>
          )}

          {/* Password Change Form */}
          {activeTab === "password" && (
            <div className="space-y-5">
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1 mb-2 block">
                  Current Password *
                </label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) =>
                    setPasswordData({
                      ...passwordData,
                      currentPassword: e.target.value,
                    })
                  }
                  placeholder="Enter current password"
                  className="w-full bg-white border border-slate-200 px-5 py-4 rounded-2xl font-bold text-sm outline-none focus:ring-4 focus:ring-subidha-blue/10 focus:border-subidha-blue transition-all"
                />
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1 mb-2 block">
                  New Password *
                </label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) =>
                    setPasswordData({
                      ...passwordData,
                      newPassword: e.target.value,
                    })
                  }
                  placeholder="Enter new password (min 8 characters)"
                  className="w-full bg-white border border-slate-200 px-5 py-4 rounded-2xl font-bold text-sm outline-none focus:ring-4 focus:ring-subidha-blue/10 focus:border-subidha-blue transition-all"
                />
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1 mb-2 block">
                  Confirm New Password *
                </label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) =>
                    setPasswordData({
                      ...passwordData,
                      confirmPassword: e.target.value,
                    })
                  }
                  placeholder="Confirm new password"
                  className="w-full bg-white border border-slate-200 px-5 py-4 rounded-2xl font-bold text-sm outline-none focus:ring-4 focus:ring-subidha-blue/10 focus:border-subidha-blue transition-all"
                />
              </div>

              <button
                onClick={handlePasswordChange}
                disabled={loading}
                className="w-full px-6 py-4 rounded-2xl font-black text-white bg-subidha-blue hover:bg-slate-800 transition-all shadow-lg shadow-blue-500/20 disabled:opacity-50 uppercase text-xs tracking-widest flex items-center justify-center space-x-2"
              >
                {loading ? (
                  <Loader2 className="animate-spin" size={20} />
                ) : (
                  <>
                    <Lock size={16} />
                    <span>Change Password</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 md:p-8 bg-slate-50 border-t border-slate-100 flex justify-between items-center">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
            Account ID: <span className="text-slate-900 ml-1">{user?.id}</span>
          </p>
          <button
            onClick={onClose}
            className="px-8 py-3 bg-[#0F172A] text-white rounded-xl font-black text-xs uppercase tracking-[0.2em] hover:bg-black transition-all"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;
